import { motion } from "framer-motion";
import {
    Flame,
    Crosshair,
    ScrollText,
    Star,
    Dumbbell,
    Brain,
} from "lucide-react";
import { Eyebrow, Reveal, EASE } from "./Reveal";

const PROGRAMS = [
    {
        icon: Flame,
        title: "Advanced Asanas",
        desc: "Master advanced yogasana techniques with proper form and controlled execution.",
    },
    {
        icon: Crosshair,
        title: "Perfect Alignment",
        desc: "Improve posture, balance, precision and technical alignment.",
    },
    {
        icon: ScrollText,
        title: "Competition Rules",
        desc: "Understand competition requirements, rules and technical expectations.",
    },
    {
        icon: Star,
        title: "Scoring & Presentation",
        desc: "Learn how to present your asana with confidence, control and precision.",
    },
    {
        icon: Dumbbell,
        title: "Strength, Flexibility & Stamina",
        desc: "Build the physical foundation required for advanced yogasana performance.",
    },
    {
        icon: Brain,
        title: "Mental Focus, Breathing & Meditation",
        desc: "Develop concentration, breathing control and mental discipline.",
    },
];

export default function Training() {
    return (
        <section id="training" data-testid="training-section" className="relative bg-[#0A0A0A] py-20 sm:py-28 lg:py-32">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_20%,rgba(232,137,22,0.05),transparent_55%)]" />
            <div className="relative mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
                <Eyebrow>What You Train</Eyebrow>
                <Reveal>
                    <h2 className="mt-6 max-w-2xl font-display text-4xl leading-[1.02] tracking-wide sm:text-5xl lg:text-6xl">
                        ADVANCED <span className="text-gold-grad">TRAINING PROGRAMS</span>
                    </h2>
                    <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[#B8B8B8]">
                        Everything you need to transform your practice into
                        competition-ready performance.
                    </p>
                </Reveal>

                <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
                    {PROGRAMS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <motion.div
                                key={p.title}
                                data-testid="training-card"
                                className="group relative overflow-hidden rounded-2xl border border-white/[0.07] bg-[#0E0E0E]/90 p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-[#D4A017]/45 hover:shadow-[0_20px_50px_-20px_rgba(212,160,23,0.3)]"
                                initial={{ opacity: 0, y: 36 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.2 }}
                                transition={{ duration: 0.7, delay: (i % 3) * 0.09, ease: EASE }}
                            >
                                <span className="pointer-events-none absolute -top-3 right-4 font-display text-7xl text-white/[0.05] transition-colors duration-500 group-hover:text-[#F5C542]/[0.12]">
                                    0{i + 1}
                                </span>

                                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4A017]/40 bg-[#D4A017]/[0.07] transition-all duration-500 group-hover:shadow-[0_0_22px_rgba(245,197,66,0.35)]">
                                    <Icon size={19} className="text-[#F5C542]" />
                                </span>

                                <h3 className="mt-6 font-display text-2xl tracking-wide text-white">
                                    {p.title.toUpperCase()}
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#B8B8B8]">
                                    {p.desc}
                                </p>

                                <div className="mt-6 h-px w-0 bg-gradient-to-r from-[#F5C542] to-transparent transition-all duration-700 group-hover:w-full" />
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
